import { FunctionComponent, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';

import {
  getAppealCategories,
  getVettingSettings,
} from './applications/CspPortal/CSP-Form/apiServices';
import {
  SET_APPEAL_CATEGORIES,
  SET_BRAND_IDENTITY_STATUS,
  SET_COUNTRIES,
  SET_DCAS,
  SET_LOA_STATUS,
  SET_PATENT_OFFICES,
  SET_STOCK_EXCHANGES,
  SET_TRADEMARK_VERIFICATION_STATUS,
  SET_VERTICAL_TYPES,
  SET_VETTING_SETTINGS,
} from './applications/CspPortal/CSP-Form/actions';
import {
  getArticle,
  getHelpAsides,
  getSimpleContent,
  getTermsAndConditions,
} from './shared_elements/apiServices';
import {
  SET_KNOWLEDGEBASE,
  SET_HELPBAR,
  SET_DYNAMIC_CONTENT,
  SET_TERMS_CONDITIONS,
} from './shared_elements/actions';
import { setupRedirectInterceptor } from './utils/apiInterceptor';
import { dcaController, enumController } from '@/apis/core';
import { getCountryOptions } from '@/apis/services';
import { useAuthContext } from '@/contexts';

const Initializer: FunctionComponent = () => {
  const { profile } = useAuthContext();
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setupRedirectInterceptor(history);
  }, [history]);

  useEffect(() => {
    (async () => {
      if (!authState || authState.isLoading) {
        return;
      }

      if (authState.isAuthenticated) {
        const [
          countriesResult,
          brandIdentityStatusResult,
          loaStatusResult,
          patentOfficesResult,
          stockExchangesResult,
          trademarkVerficationStatusResult,
          verticalTypesResult,
          appealCategoriesResult,
          vettingSettingsResult,
          articleResult,
          helpAsidesResult,
          simpleContentResult,
          termsAndConditionsResult,
        ] = await Promise.allSettled([
          getCountryOptions(),
          enumController.getBrandIdentityStatus(),
          enumController.getLoaStatus(),
          enumController.getPatentOffices(),
          enumController.getStockExchanges(),
          enumController.getTrademarkVerificationStatus(),
          enumController.getVerticalTypes(),
          getAppealCategories(),
          getVettingSettings(),
          getArticle(),
          getHelpAsides(),
          getSimpleContent(),
          getTermsAndConditions(),
        ]);
        if (countriesResult.status === 'fulfilled') {
          dispatch({
            type: SET_COUNTRIES,
            payload: countriesResult.value,
          });
        }
        if (brandIdentityStatusResult.status === 'fulfilled') {
          dispatch({
            type: SET_BRAND_IDENTITY_STATUS,
            payload: brandIdentityStatusResult.value,
          });
        }
        if (loaStatusResult.status === 'fulfilled') {
          dispatch({
            type: SET_LOA_STATUS,
            payload: loaStatusResult.value,
          });
        }
        if (patentOfficesResult.status === 'fulfilled') {
          dispatch({
            type: SET_PATENT_OFFICES,
            payload: patentOfficesResult.value,
          });
        }
        if (stockExchangesResult.status === 'fulfilled') {
          dispatch({
            type: SET_STOCK_EXCHANGES,
            payload: stockExchangesResult.value,
          });
        }
        if (trademarkVerficationStatusResult.status === 'fulfilled') {
          dispatch({
            type: SET_TRADEMARK_VERIFICATION_STATUS,
            payload: trademarkVerficationStatusResult.value,
          });
        }
        if (verticalTypesResult.status === 'fulfilled') {
          dispatch({
            type: SET_VERTICAL_TYPES,
            payload: verticalTypesResult.value,
          });
        }
        if (appealCategoriesResult.status === 'fulfilled') {
          dispatch({
            type: SET_APPEAL_CATEGORIES,
            payload: appealCategoriesResult.value,
          });
        }
        if (vettingSettingsResult.status === 'fulfilled') {
          dispatch({
            type: SET_VETTING_SETTINGS,
            payload: vettingSettingsResult.value,
          });
        }
        if (articleResult.status === 'fulfilled') {
          dispatch({
            type: SET_KNOWLEDGEBASE,
            payload: articleResult.value.data.filter((item: any) =>
              ['review', 'published'].includes(item.status)
            ),
          });
        }
        if (helpAsidesResult.status === 'fulfilled') {
          dispatch({
            type: SET_HELPBAR,
            payload: helpAsidesResult.value.data.filter((item: any) =>
              ['review', 'published'].includes(item.status)
            ),
          });
        }
        if (simpleContentResult.status === 'fulfilled') {
          dispatch({
            type: SET_DYNAMIC_CONTENT,
            payload: simpleContentResult.value.data.filter((item: any) =>
              ['review', 'published'].includes(item.status)
            ),
          });
        }
        if (termsAndConditionsResult.status === 'fulfilled') {
          dispatch({
            type: SET_TERMS_CONDITIONS,
            payload: termsAndConditionsResult.value.data.filter((item: any) =>
              ['review', 'published'].includes(item.status)
            ),
          });
        }
      }
    })();
  }, [authState?.isLoading, authState?.isAuthenticated]);

  useEffect(() => {
    (async () => {
      if (profile?.status === 'ACTIVE') {
        const [dcasResult] = await Promise.allSettled([
          dcaController.getDcas(),
        ]);
        if (dcasResult.status === 'fulfilled') {
          dispatch({
            type: SET_DCAS,
            payload: dcasResult.value,
          });
        }
      }
    })();
  }, [profile?.status]);

  return null;
};

export default Initializer;
